import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Components
import SubsectionTitle from 'components/SubsectionTitle';
import { ClipLoader } from 'react-spinners';
import Error from 'components/Error';
import DonutChart from 'components/DonutChart';

// Translations
import { injectIntl } from 'react-intl';
import messages from '../messages';

// APIs
import { fetchEngagement } from '../api';

// Images
import { ReactComponent as EngagementIco } from 'images/analytics/engagement.svg';

// Constants
import * as Colors from 'constants/colors';

// Data
import legendData from 'containers/Analytics/engagementLegend';

// Style
const Wrapper = styled.div`
  display: flex;
  margin-top: 40px;
`;

const Content = styled.div`
  flex: 1;
  margin-top: 30px;
  margin-left: 24px;
  display: flex;
  justify-content: center;
`;

const EngagementIcon = styled(EngagementIco)`
  width: 50px;
  height: 50px;
`;

function Engagement({ intl, unitId, start, end }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(undefined);
  const [error, setError] = useState(false);

  useEffect(() => {
    let canceled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await fetchEngagement(unitId, start, end);
        const json = await response.json();
        if (!canceled) {
          setData(json);
        }
      } catch {
        if (!canceled) {
          setError(true);
        }
      } finally {
        if (!canceled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      canceled = true;
    };
  }, [unitId, start, end]);

  const renderLoading = () => {
    if (loading) {
      return <ClipLoader size={20} color={Colors.LIGHT_NAVY} />;
    }
    return null;
  };

  const renderDonut = () => {
    if (!loading && !error && data) {
      return <DonutChart data={data} legendData={legendData} />;
    }
    return null;
  };

  const renderError = () => {
    if (error) {
      return <Error />;
    }
    return null;
  };

  return (
    <Wrapper>
      <SubsectionTitle
        icon={<EngagementIcon />}
        title={intl.formatMessage(messages.engagementTitle)}
        body={intl.formatMessage(messages.engagementSubtitle)}
      />

      <Content>
        {renderLoading()}
        {renderDonut()}
        {renderError()}
      </Content>
    </Wrapper>
  );
}

Engagement.propTypes = {
  intl: PropTypes.object,
  unitId: PropTypes.number,
  start: PropTypes.string,
  end: PropTypes.string,
};

export default injectIntl(Engagement);
